import React, {Component, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import {colors} from '@atlaskit/theme';
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};
const grid = 4;

const Table = styled.table`
  margin: 0 auto;
  table-layout: ${(props) => props.layout};
`;

const TBody = styled.tbody`
  border: 0;
`;

const THead = styled.thead`
  border: 0;
  border-bottom: none;
`;

export const Row = styled.tr`

`;

const Cell = styled.td`
  box-sizing: border-box;
  padding: ${grid}px;
`;

const snapshotMap = {};

export class TableCell extends React.Component {
    ref;

    getSnapshotBeforeUpdate(prevProps) {
        if (!this.ref) {
            return null;
        }

        const isDragStarting =
            this.props.isDragOccurring && !prevProps.isDragOccurring;

        if (!isDragStarting) {
            return null;
        }

        const {width, height} = this.ref.getBoundingClientRect();

        const snapshot = {
            width,
            height,
        };

        return snapshot;
    }

    componentDidUpdate(
        prevProps,
        prevState,
        snapshot,
    ) {
        const ref = this.ref;
        if (!ref) {
            return;
        }

        if (snapshot) {
            if (ref.style.width === snapshot.width) {
                return;
            }
            ref.style.width = `${snapshot.width}px`;
            ref.style.height = `${snapshot.height}px`;
            return;
        }

        if (this.props.isDragOccurring) {
            return;
        }

        // inline styles not applied
        if (ref.style.width == null) {
            return;
        }

        // no snapshot and drag is finished - clear the inline styles
        ref.style.removeProperty('height');
        ref.style.removeProperty('width');
    }

    setRef = (ref) => {
        this.ref = ref;
    };

    render() {
        return <Cell ref={this.setRef} className={this.props.className}>{this.props.children}</Cell>;
    }
}

// Using a table as the portal so that we do not get react
// warnings when mounting a tr element
const table = document.createElement('table');
table.classList.add('my-super-cool-table-portal');
Object.assign(table.style, {
    margin: '0',
    padding: '0',
    border: '0',
    height: '0',
    width: '0',
});
const tbody = document.createElement('tbody');
table.appendChild(tbody);

if (!document.body) {
    throw new Error('document.body required for example');
}
document.body.appendChild(table);

const IsDraggingContext = React.createContext(false);

class TableRow extends Component {
    render() {
        const {index, snapshot, row, rowRenderer, provided} = this.props;
        return (
            <IsDraggingContext.Consumer>
                {(isDragging) => (rowRenderer(index, row, isDragging, provided, snapshot))}
            </IsDraggingContext.Consumer>
        );
    }
}

export default function TableWithDraggableRows({
                                                   rows,
                                                   columns,
                                                   className,
                                                   isDragEnabled,
                                                   rowIdGetter,
                                                   rowRenderer,
                                                   onReorder
                                               }) {
    const [isDragging, setIsDragging] = useState(false);
    const [localRows, setLocalRows] = useState([]);

    useEffect(() => {
        setLocalRows(rows);
    }, [rows])

    function onBeforeDragStart() {
        setIsDragging(true);
    }

    function onDragEnd(result) {
        setIsDragging(false);

        // dropped outside the list
        if (
            !result.destination ||
            result.destination.index === result.source.index
        ) {
            return;
        }

        // no movement
        if (result.destination.index === result.source.index) {
            return;
        }

        const newRows = reorder(
            localRows,
            result.source.index,
            result.destination.index,
        );

        setLocalRows(newRows);

        if (onReorder)
            onReorder(newRows);
    }

    return (
        <IsDraggingContext.Provider value={isDragging}>
            <DragDropContext
                onBeforeDragStart={onBeforeDragStart}
                onDragEnd={onDragEnd}
            >
                <Table className={className} layout="auto">
                    <THead>
                        <tr>
                            {columns.map((column, index) => (
                                <td className="table-column" key={`col-${index}`}>
                                    <div className="table-column-content">
                                        {column.title}
                                    </div>
                                </td>
                            ))}
                        </tr>
                    </THead>
                    <tbody>
                    <tr>
                        <td className="spacer-tbody-tr"/>
                    </tr>
                    </tbody>
                    <Droppable droppableId="table">
                        {(droppableProvided) => (
                            <TBody
                                ref={(ref) => {
                                    droppableProvided.innerRef(ref);
                                }}
                                {...droppableProvided.droppableProps}
                            >
                                {localRows.map((row, index) => (
                                    <Draggable
                                        draggableId={rowIdGetter(row)}
                                        index={index}
                                        key={`row-${rowIdGetter(row)}`}
                                        isDragDisabled={!isDragEnabled}>
                                        {(
                                            provided,
                                            snapshot,
                                        ) => (
                                            <TableRow
                                                index={index}
                                                provided={provided}
                                                snapshot={snapshot}
                                                row={row}
                                                rowRenderer={rowRenderer}
                                            />
                                        )}
                                    </Draggable>
                                ))}
                                {droppableProvided.placeholder}
                            </TBody>
                        )}
                    </Droppable>
                </Table>
            </DragDropContext>
        </IsDraggingContext.Provider>
    );
}